import * as pdfMake from "pdfmake/build/pdfmake.js";

import DateUtil from "@/plugins/date-util/DateUtil";

/**
 * ### 文書送付、確認・同意ユーザー一覧のPDF操作
 */
export class SendDocumentsOptionTargetOtherUsersPdf {
  dateUtil: DateUtil;
  normalFont: string;

  /**
   * ### コンストラクター
   *
   * - 渡されたフォントをPdfMakeに設定する
   */
  constructor(normalFont: string) {
    this.dateUtil = new DateUtil();
    this.normalFont = normalFont;

    pdfMake.vfs = { "SendDocumentPdf.ttf": this.normalFont };
    pdfMake.fonts = {
      SendDocumentPdf: {
        normal: "SendDocumentPdf.ttf",
        bold: "SendDocumentPdf.ttf"
      }
    };

    return;
  }

  public createAndDownloadPdf(
    items: Array<OptionTargetOtherUserTableItem>,
    facilityName: string,
    title: string,
    sendDocumentDatetime: string,
    type: OptionTargetOtherUserListModalType,
    fileName: string
  ): void {
    const docDefinition: any = {
      styles: this.createStyles(),
      defaultStyle: this.createDefaultStyle(),
      pageSize: "A4",
      pageOrientation: "portrait",
      pageMargins: [27, 14, 27, 27], // [left, top, right, bottom]
      info: this.createInfo(),
      content: this.createContent(items, facilityName, title, sendDocumentDatetime, type),
      footer: this.createFooter()
    };

    pdfMake.createPdf(docDefinition).download(fileName);

    return;
  }

  /**
   * ### PDFを詳細形式（同意者ごと・添付書類画像付き）で作成・ダウンロードする（出力B）
   */
  public createAndDownloadDetailPdf(
    items: Array<OptionTargetOtherUserTableItem>,
    facilityName: string,
    title: string,
    sendDocumentDatetime: string,
    type: OptionTargetOtherUserListModalType,
    fileName: string,
    imagesMap: Map<string, Array<string>>
  ): void {
    const docDefinition: any = {
      styles: this.createStyles(),
      defaultStyle: this.createDefaultStyle(),
      pageSize: "A4",
      pageOrientation: "portrait",
      pageMargins: [27, 14, 27, 27], // [left, top, right, bottom]
      info: this.createInfo(),
      content: this.createDetailContent(items, facilityName, title, sendDocumentDatetime, type, imagesMap),
      footer: this.createFooter()
    };

    pdfMake.createPdf(docDefinition).download(fileName);

    return;
  }

  /**
   * ### 詳細形式の印刷情報を作成する（出力B）
   */
  private createDetailContent(
    items: Array<OptionTargetOtherUserTableItem>,
    facilityName: string,
    title: string,
    sendDocumentDatetime: string,
    type: OptionTargetOtherUserListModalType,
    imagesMap: Map<string, Array<string>>
  ): Array<any> {
    const contents: Array<any> = [];

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const isFirstItem = i === 0;

      // 同意者ごとに新しいページから開始（1人目以外）
      const headerContents = this.createHeader(facilityName, title, sendDocumentDatetime, type);
      if (!isFirstItem && headerContents.length > 0) {
        headerContents[0].pageBreak = "before";
      }
      contents.push(...headerContents);

      // 同意者1人分の1行テーブルを作成
      contents.push(this.createSingleRowTable(item, type));

      // 同意者に関連する添付ファイル画像のリストを取得
      const itemImages = imagesMap.get(item.otherUserGroupsSortKey) || imagesMap.get(String(item.id)) || imagesMap.get("default") || [];

      if (itemImages.length > 0) {
        const gridElements = this.createImagesGrid(itemImages);
        contents.push(...gridElements);
      }
    }

    return contents;
  }

  /**
   * ### 添付画像群（1〜9件）を1ページ内に集約配置するオブジェクトを作成する
   */
  private createImagesGrid(itemImages: Array<string>): Array<any> {
    if (!itemImages || itemImages.length === 0) {
      return [];
    }

    const count = Math.min(itemImages.length, 9);
    const validImages = itemImages.slice(0, count);

    // 行構成の定義 (1〜9件)
    const rowConfigurations: { [key: number]: number[] } = {
      1: [1],
      2: [2],
      3: [2, 1],
      4: [2, 2],
      5: [3, 2],
      6: [3, 3],
      7: [3, 3, 1],
      8: [3, 3, 2],
      9: [3, 3, 3]
    };

    const rowCounts = rowConfigurations[count] || [1];
    const numRows = rowCounts.length;

    // 1ページ内で画像を配置可能な最大領域
    const totalWidth = 538;
    const totalHeight = 540;

    // 行ごとの最大高さ
    const rowGap = 10;
    const availableHeightForRow = (totalHeight - (numRows - 1) * rowGap) / numRows;

    const resultGrid: Array<any> = [];
    let imageIndex = 0;

    for (let r = 0; r < numRows; r++) {
      const itemsInRow = rowCounts[r];
      const maxColsInLayout = Math.max(...rowCounts);
      const colGap = 10;

      // 3列または2列レイアウト時の基準幅
      const colWidth = (totalWidth - (maxColsInLayout - 1) * colGap) / maxColsInLayout;
      const fitWidth = Math.floor(colWidth);
      const fitHeight = Math.floor(availableHeightForRow);

      const columns: Array<any> = [];

      // 中央揃え用のマージン（この行の要素数が最大列数より少ない場合）
      if (itemsInRow < maxColsInLayout) {
        const rowWidth = itemsInRow * fitWidth + (itemsInRow - 1) * colGap;
        const leftSpacer = (totalWidth - rowWidth) / 2;
        columns.push({ text: "", width: leftSpacer });
      }

      for (let c = 0; c < itemsInRow; c++) {
        if (imageIndex >= validImages.length) break;
        const imgDataUrl = validImages[imageIndex];
        imageIndex++;

        columns.push({
          width: fitWidth,
          table: {
            widths: [fitWidth],
            body: [
              [
                {
                  image: imgDataUrl,
                  fit: [fitWidth, fitHeight],
                  alignment: "center"
                }
              ]
            ]
          },
          layout: {
            hLineWidth: () => 1,
            vLineWidth: () => 1,
            hLineColor: () => "#000000",
            vLineColor: () => "#000000",
            paddingLeft: () => 0,
            paddingRight: () => 0,
            paddingTop: () => 0,
            paddingBottom: () => 0
          },
          alignment: "center"
        });

        if (c < itemsInRow - 1) {
          columns.push({ text: "", width: colGap });
        }
      }

      resultGrid.push({
        columns: columns,
        margin: [0, r === 0 ? 10 : rowGap, 0, 0]
      });
    }

    return resultGrid;
  }

  /**
   * ### 1人分のテーブル情報を作成する（出力B）
   */
  private createSingleRowTable(item: OptionTargetOtherUserTableItem, type: OptionTargetOtherUserListModalType): any {
    // 同意or確認済みの場合、連絡内容には固定文言を追加する
    let message = this.getMessagePrefix(item, type);
    if (item.message) {
      message += item.message;
    }

    const tableContent: any = {
      style: "table",
      table: {
        headerRows: 1,
        widths: ["*", "*", 110, "*"]
      }
    };
    const body: Array<any> = [
      [
        { text: "施設関係者グループ", style: "tableHeader" },
        { text: "施設関係者メンバー", style: "tableHeader" },
        { text: this.getReplyDatetimeRowName(type), style: "tableHeader" },
        { text: "返信時の連絡事項", style: "tableHeader" }
      ],
      [`${item.name}（${item.groupTypeName}）`, item.memberName, { text: this.convertDateText(item.datetime), style: { alignment: "center" } }, message]
    ];
    tableContent.table.body = body;

    return tableContent;
  }

  /**
   * ### PDFのスタイルを作成する
   *
   * - 項目毎、または共通で使用できるスタイルを指定する
   */
  private createStyles(): any {
    return {
      datetime: {
        fontSize: 12,
        alignment: "right",
        margin: [0, 0, 0, 5]
      },
      title: {
        fontSize: 18,
        bold: true,
        alignment: "center",
        margin: [0, 0, 0, 18]
      },
      names: {
        fontSize: 12
      },
      table: {
        fontSize: 10,
        margin: [0, 10, 0, 0]
      },
      tableHeader: {
        bold: true,
        fillColor: "#EEEEEE",
        alignment: "center",
        margin: [2, 2, 2, 2]
      }
    };
  }

  /**
   * ### PDFの共通スタイルを作成する
   *
   * - 全体で共通で使用できるスタイルを指定する
   */
  private createDefaultStyle(): any {
    return {
      font: "SendDocumentPdf"
    };
  }

  /**
   * ### PDF文書情報を作成する
   */
  private createInfo(): any {
    return {
      title: "HitomeQコネクト 文書送付一覧",
      author: "KONICAMINOLA"
    };
  }

  /**
   * ### フッター情報を作成する
   *
   * - ページ番号を印字する
   */
  private createFooter(): Function {
    return (currentPage: number, pageCount: number) => {
      return { text: currentPage.toString() + " / " + pageCount + "ページ", style: { fontSize: 12, alignment: "center", margin: [0, 12, 0, 12] } };
    };
  }

  /**
   * ### 印刷情報を作成する
   *
   * - ヘッダー部
   * - コンテンツ部(文書送付情報)
   */
  private createContent(
    items: Array<OptionTargetOtherUserTableItem>,
    facilityName: string,
    title: string,
    sendDocumentDatetime: string,
    type: OptionTargetOtherUserListModalType
  ): any {
    let contents: Array<any> = [];

    contents = this.createHeader(facilityName, title, sendDocumentDatetime, type);
    contents.push(this.createTable(items, type));

    return contents;
  }

  /**
   * ### コンテンツのタイトル及び固定部分を作成する
   */
  private createHeader(facilityName: string, title: string, sendDocumentDatetime: string, type: OptionTargetOtherUserListModalType): Array<any> {
    const contents: Array<any> = [];

    // 日付
    contents.push({
      text: this.pdfCreateDateTime(),
      style: "datetime"
    });

    // タイトル
    contents.push({
      text: this.createPdfTitle(type),
      style: "title"
    });

    // 施設名、文書タイトル、送信日時
    contents.push({
      text: ["施設名：", { text: `${facilityName}`, bold: true }],
      style: "names"
    });
    contents.push({
      text: ["タイトル：", { text: `${title}`, bold: true }],
      style: "names"
    });
    contents.push({
      text: ["送信日時：", { text: `${sendDocumentDatetime}`, bold: true }],
      style: "names"
    });

    return contents;
  }

  /**
   * ### 作成日時を「yyyy年 mm月 dd日」の形式の文字列にする
   */
  private pdfCreateDateTime(): string {
    const datetime: string = this.dateUtil.convertDateToString(new Date(), "yyyy年 mm月 dd日", false);
    return datetime;
  }

  /**
   * ### PDFタイトルを作成する
   *
   * @param type 種別
   */
  private createPdfTitle(type: OptionTargetOtherUserListModalType): string {
    switch (type) {
      case OptionTargetOtherUserListModalType.AGREEMENT: {
        return "HitomeQコネクト 文書送付　同意者一覧";
      }
      case OptionTargetOtherUserListModalType.CONFIRM: {
        return "HitomeQコネクト 文書送付　確認者一覧";
      }
      default: {
        return "";
      }
    }
  }

  /**
   * ### テーブル情報を作成する
   *
   * @param items 文書送付情報
   */
  private createTable(items: Array<OptionTargetOtherUserTableItem>, type: OptionTargetOtherUserListModalType): any {
    // テーブル
    const tableContent: any = {
      style: "table",
      table: {
        headerRows: 1,
        widths: ["*", "*", 110, "*"]
      }
    };
    const body: Array<any> = [
      [
        { text: "施設関係者グループ", style: "tableHeader" },
        { text: "施設関係者メンバー", style: "tableHeader" },
        { text: this.getReplyDatetimeRowName(type), style: "tableHeader" },
        { text: "返信時の連絡事項", style: "tableHeader" }
      ]
    ];
    for (const item of items) {
      // 連絡内容には固定文言を追加する
      let message = this.getMessagePrefix(item, type);
      if (item.message) {
        message += item.message;
      }

      body.push([`${item.name}（${item.groupTypeName}）`, item.memberName, { text: this.convertDateText(item.datetime), style: { alignment: "center" } }, message]);
    }
    tableContent.table.body = body;

    return tableContent;
  }

  /**
   * ### 返信日時の列名を返す
   */
  private getReplyDatetimeRowName(type: OptionTargetOtherUserListModalType): string {
    if (type == OptionTargetOtherUserListModalType.AGREEMENT) {
      return "同意の返信日時";
    } else if (type == OptionTargetOtherUserListModalType.CONFIRM) {
      return "同意の確認日時";
    } else {
      return "";
    }
  }

  /**
   * ### 日付を「yyyy年mm月dd日 hh:MM」形式の文字列に変換する
   *
   * @param datetime 日付
   */
  private convertDateText(datetime: number | null): string {
    if (datetime == null) {
      return "ー";
    }

    const date: string = this.dateUtil.convertNumberToString(datetime, "yyyy年mm月dd日 hh:MM");

    return date;
  }

  /**
   * ### 同意or確認済みの場合、固定文言を返す
   */
  private getMessagePrefix(item: OptionTargetOtherUserTableItem, type: OptionTargetOtherUserListModalType): string {
    if (item.datetime == null) {
      return "";
    }

    if (type == OptionTargetOtherUserListModalType.AGREEMENT) {
      return "文書内容に同意します。\n";
    } else if (type == OptionTargetOtherUserListModalType.CONFIRM) {
      return "文書内容を確認しました。\n";
    } else {
      return "";
    }
  }
}

/** テーブルの1行あたりの情報 */
export interface OptionTargetOtherUserTableItem {
  /** 施設関係者グループソートキー */
  otherUserGroupsSortKey: string;
  /** グループを一意に識別する ID */
  id: number;
  /** グループ名 */
  name: string;
  /** グループの種類の ID。otherUserGroupTypes の id とひもづく */
  groupTypeId: number;
  /** グループ種類名 */
  groupTypeName: string;
  /** グループ内のメンバーを一意に識別する ID */
  memberId: number;
  /** メンバー名 */
  memberName: string;
  /** メンバー電話番号 */
  memberTel: string;
  /** メンバー所属 */
  memberOrganization: string;
  /** メンバーのLINE の ID */
  memberLineId: string | null;
  /** 返信日時 */
  datetime: number | null;
  /** 返信内容 */
  message: string;
  /** 格納元情報 */
  source: any;
}

export enum OptionTargetOtherUserListModalType {
  UNKNOWN = -1,
  AGREEMENT = 0,
  NON_AGREEMENT = 1,
  CONFIRM = 2,
  NON_CONFIRM = 3
}
